import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useToast } from './Toast';
import { useAuth } from '../auth/AuthContext';
import { extractErrorMessage } from '../utils/errors';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OsImage { id: string; name: string; osType: string; publisher: string; isRecommended: boolean; }
interface VmSize  { id: string; name: string; vCpus: number; memoryGb: number; storageGb: number; pricePerHour: number; }
interface Catalog { osImages: OsImage[]; vmSizes: VmSize[]; locations: string[]; }

interface VmSizeAvailability { sizeId: string; available: boolean; unavailableReason?: string; availableInZones: number[]; }
interface LocationCapabilities { location: string; availableVmSizes: string[]; availabilityZones: number[]; sizeDetails: VmSizeAvailability[]; }

interface DeploymentStep { name: string; status: string; message?: string; startedAt?: string; completedAt?: string; }
interface DeployedVm { id: string; name: string; powerState: string; privateIpAddress?: string; publicIpAddress?: string; vCpuCount: number; memoryGb: number; }
interface Deployment {
  deploymentId: string; vmName: string; resourceGroup: string; subscriptionId: string;
  state: 'Pending' | 'Running' | 'Succeeded' | 'Failed' | 'Cancelled';
  progressPercent: number; steps: DeploymentStep[];
  errorMessage?: string; deployedVm?: DeployedVm;
  startedAt: string; completedAt?: string;
}

const STEP_STATUS_COLOR: Record<string, string> = {
  Pending:   '#555',
  Running:   '#007bff',
  Completed: '#28a745',
  Failed:    '#dc3545',
  Skipped:   '#6c757d',
};

const STATE_COLOR: Record<string, string> = {
  Pending:   '#ffc107',
  Running:   '#007bff',
  Succeeded: '#28a745',
  Failed:    '#dc3545',
  Cancelled: '#6c757d',
};

// ── Wizard Steps ──────────────────────────────────────────────────────────────

const WIZARD_STEPS = ['Basics', 'OS & Size', 'Network', 'Tags', 'Review'];

// ── Deployment Templates ──────────────────────────────────────────────────────

interface DeploymentTemplate {
  id: string;
  name: string;
  savedAt: string;
  form: Omit<FormData, 'name'>;
}

const TEMPLATES_KEY = 'vm_deployment_templates';

function loadTemplates(): DeploymentTemplate[] {
  try { return JSON.parse(localStorage.getItem(TEMPLATES_KEY) ?? '[]'); }
  catch { return []; }
}

function saveTemplate(t: DeploymentTemplate) {
  const all = loadTemplates().filter(x => x.id !== t.id);
  localStorage.setItem(TEMPLATES_KEY, JSON.stringify([t, ...all].slice(0, 20)));
}

function deleteTemplate(id: string) {
  const all = loadTemplates().filter(t => t.id !== id);
  localStorage.setItem(TEMPLATES_KEY, JSON.stringify(all));
}

interface SubscriptionOption { id: string; name: string; }
interface RGOption    { name: string; location: string; }
interface VNetOption2 { name: string; resourceGroup: string; addressSpace: string; }
interface SubnetOption2 { name: string; addressPrefix: string; }

interface FormData {
  name: string;
  subscriptionId: string;
  resourceGroup: string; createNewResourceGroup: boolean;
  location: string; availabilityZone: number | null;
  vmSize: string; osType: string; osImage: string;
  adminUsername: string; authType: string; sshPublicKey: string; adminPassword: string;
  osDiskSizeGb: number; osDiskType: string;
  vnetName: string; createNewVnet: boolean; vnetAddressSpace: string;
  subnetName: string; createNewSubnet: boolean; subnetAddressPrefix: string;
  assignPublicIp: boolean;
  tags: { key: string; value: string }[];
}

const defaultForm = (): FormData => ({
  name: '', subscriptionId: '', resourceGroup: '', createNewResourceGroup: false,
  location: 'westeurope', availabilityZone: null, vmSize: '', osType: 'Linux', osImage: '',
  adminUsername: 'azureuser', authType: 'SshKey', sshPublicKey: '', adminPassword: '',
  osDiskSizeGb: 64, osDiskType: 'Premium_LRS',
  vnetName: '', createNewVnet: false, vnetAddressSpace: '10.0.0.0/16',
  subnetName: '', createNewSubnet: false, subnetAddressPrefix: '10.0.1.0/24',
  assignPublicIp: false,
  tags: [{ key: 'environment', value: 'production' }],
});

// ── SelectOrCreate ─────────────────────────────────────────────────────────────
// Shared dropdown-with-create-option control

interface SelectOrCreateProps {
  label: string;
  required?: boolean;
  options: { value: string; label: string; sublabel?: string }[];
  selectedValue: string;
  isCreating: boolean;
  newValue: string;
  newPlaceholder: string;
  extraField?: React.ReactNode;   // shown below text input when creating (e.g. address space)
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  onSelect: (value: string) => void;
  onCreate: () => void;
  onNewValueChange: (v: string) => void;
  onCancelCreate?: () => void;
}

const SelectOrCreate: React.FC<SelectOrCreateProps> = ({
  label, required, options, selectedValue, isCreating, newValue, newPlaceholder,
  extraField, error, disabled, loading, emptyMessage,
  onSelect, onCreate, onNewValueChange, onCancelCreate,
}) => {
  const selectValue = isCreating ? '__new__' : selectedValue;
  const fieldStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box' as const };

  return (
    <div>
      <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>
        {label}{required && ' *'}
        {loading && <span style={{ marginLeft: 8, color: '#555', fontWeight: 400 }}>Loading…</span>}
      </label>
      <select
        value={selectValue}
        onChange={e => e.target.value === '__new__' ? onCreate() : onSelect(e.target.value)}
        disabled={disabled || loading}
        style={fieldStyle}
      >
        <option value="">— Select {label.toLowerCase()} —</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>
            {o.label}{o.sublabel ? ` (${o.sublabel})` : ''}
          </option>
        ))}
        {options.length === 0 && emptyMessage && (
          <option disabled value="">{emptyMessage}</option>
        )}
        <option value="__new__">➕  Create new {label.toLowerCase()}…</option>
      </select>

      {isCreating && (
        <div style={{ marginTop: 6, padding: '0.75rem', background: '#1a1a1e', borderRadius: 6, border: '1px solid #007bff44' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: '0.8rem', color: '#4da6ff' }}>New {label.toLowerCase()}</span>
            {onCancelCreate && (
              <button type="button" onClick={onCancelCreate}
                style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>
                ✕ Cancel
              </button>
            )}
          </div>
          <input value={newValue} onChange={e => onNewValueChange(e.target.value)} placeholder={newPlaceholder} style={fieldStyle} />
          {extraField}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 4, fontSize: '0.75rem', color: '#ff8f9a' }}>{error}</div>
      )}
    </div>
  );
};

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

// ── Deployment Progress Panel ─────────────────────────────────────────────────

interface ProgressPanelProps {
  deployment: Deployment;
  onClose: () => void;
  onCancel: (id: string) => void;
}

const ProgressPanel: React.FC<ProgressPanelProps> = ({ deployment, onClose, onCancel }) => {
  const done = deployment.state === 'Succeeded' || deployment.state === 'Failed' || deployment.state === 'Cancelled';

  return (
    <div style={{ background: '#25252a', border: `1px solid ${STATE_COLOR[deployment.state]}66`, borderRadius: 8, padding: '1.5rem', marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
        <div>
          <h3 style={{ margin: 0 }}>Deploying: {deployment.vmName}</h3>
          <div style={{ color: '#888', fontSize: '0.85rem', marginTop: 2 }}>{deployment.resourceGroup} · {deployment.subscriptionId}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ background: STATE_COLOR[deployment.state] + '22', color: STATE_COLOR[deployment.state], border: `1px solid ${STATE_COLOR[deployment.state]}66`, borderRadius: 12, padding: '0.25rem 0.75rem', fontSize: '0.8rem', fontWeight: 600 }}>
            {deployment.state}
          </span>
          {!done && <button className="btn-small btn-danger" onClick={() => onCancel(deployment.deploymentId)}>Cancel</button>}
          {done && <button className="btn-small btn-secondary" onClick={onClose}>Close</button>}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ background: '#1a1a1e', borderRadius: 6, height: 8, marginBottom: '1rem', overflow: 'hidden' }}>
        <div style={{ width: `${deployment.progressPercent}%`, height: '100%', background: STATE_COLOR[deployment.state], transition: 'width 0.4s ease', borderRadius: 6 }} />
      </div>

      {/* Steps */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {deployment.steps.map((step, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.85rem' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: STEP_STATUS_COLOR[step.status], flexShrink: 0, boxShadow: step.status === 'Running' ? `0 0 6px ${STEP_STATUS_COLOR[step.status]}` : undefined }} />
            <span style={{ color: step.status === 'Completed' ? '#ccc' : step.status === 'Running' ? '#fff' : '#666', flex: 1 }}>{step.name}</span>
            {step.message && <span style={{ color: '#888', fontSize: '0.8rem', maxWidth: 300, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{step.message}</span>}
            <span style={{ color: '#555', fontSize: '0.75rem', width: 80, textAlign: 'right', flexShrink: 0 }}>{step.status}</span>
          </div>
        ))}
      </div>

      {deployment.errorMessage && (
        <div style={{ marginTop: '1rem', background: '#dc354522', border: '1px solid #dc3545', borderRadius: 6, padding: '0.75rem', color: '#ff8f9a', fontSize: '0.85rem' }}>
          {deployment.errorMessage}
        </div>
      )}

      {deployment.deployedVm && (
        <div style={{ marginTop: '1rem', background: '#28a74522', border: '1px solid #28a74566', borderRadius: 6, padding: '0.75rem' }}>
          <div style={{ color: '#90ee90', fontWeight: 600, marginBottom: 4 }}>VM deployed successfully</div>
          <div style={{ fontSize: '0.85rem', color: '#b0b0b0', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            <span>ID: {deployment.deployedVm.id}</span>
            <span>State: {deployment.deployedVm.powerState}</span>
            <span>Private IP: {deployment.deployedVm.privateIpAddress || '—'}</span>
            <span>Public IP: {deployment.deployedVm.publicIpAddress || '—'}</span>
            <span>vCPUs: {deployment.deployedVm.vCpuCount}</span>
            <span>RAM: {deployment.deployedVm.memoryGb} GB</span>
          </div>
        </div>
      )}

      <div style={{ marginTop: '0.75rem', color: '#555', fontSize: '0.75rem' }}>
        Started: {fmtDate(deployment.startedAt)}{deployment.completedAt ? ` · Completed: ${fmtDate(deployment.completedAt)}` : ''}
      </div>
    </div>
  );
};

// ── Recent Deployments ────────────────────────────────────────────────────────

interface RecentDeploymentsProps {
  deployments: Deployment[];
  onView: (d: Deployment) => void;
}

const RecentDeployments: React.FC<RecentDeploymentsProps> = ({ deployments, onView }) => (
  <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: 8, overflow: 'hidden' }}>
    <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #444', fontWeight: 600 }}>Recent Deployments</div>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#333338' }}>
          {['VM Name', 'Resource Group', 'State', 'Progress', 'Started', 'Completed'].map(h => (
            <th key={h} style={{ padding: '0.6rem 1rem', textAlign: 'left', color: '#888', fontWeight: 600, fontSize: '0.8rem' }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {deployments.length === 0 ? (
          <tr><td colSpan={6} style={{ textAlign: 'center', padding: '1.5rem', color: '#888' }}>No recent deployments.</td></tr>
        ) : deployments.map(d => (
          <tr key={d.deploymentId} style={{ cursor: 'pointer', borderBottom: '1px solid #333' }} onClick={() => onView(d)}>
            <td style={{ padding: '0.75rem 1rem', fontWeight: 500 }}>{d.vmName}</td>
            <td style={{ padding: '0.75rem 1rem', color: '#b0b0b0', fontSize: '0.85rem' }}>{d.resourceGroup}</td>
            <td style={{ padding: '0.75rem 1rem' }}>
              <span style={{ background: STATE_COLOR[d.state] + '22', color: STATE_COLOR[d.state], border: `1px solid ${STATE_COLOR[d.state]}66`, borderRadius: 12, padding: '0.2rem 0.6rem', fontSize: '0.75rem', fontWeight: 600 }}>
                {d.state}
              </span>
            </td>
            <td style={{ padding: '0.75rem 1rem' }}>
              <div style={{ background: '#333', borderRadius: 4, height: 6, width: 80, overflow: 'hidden' }}>
                <div style={{ width: `${d.progressPercent}%`, height: '100%', background: STATE_COLOR[d.state] }} />
              </div>
            </td>
            <td style={{ padding: '0.75rem 1rem', color: '#888', fontSize: '0.8rem' }}>{fmtDate(d.startedAt)}</td>
            <td style={{ padding: '0.75rem 1rem', color: '#888', fontSize: '0.8rem' }}>{fmtDate(d.completedAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

const VmDeployment: React.FC = () => {
  const { showToast } = useToast();
  const { user } = useAuth();
  // Mirrors the API policy: deployments need operator or admin.
  const canWrite = user?.role === 'operator' || user?.role === 'admin';
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [caps, setCaps] = useState<LocationCapabilities | null>(null);
  const [capsLoading, setCapsLoading] = useState(false);

  // Resource picker options
  const [subscriptions, setSubscriptions] = useState<SubscriptionOption[]>([]);
  const [resourceGroups, setResourceGroups] = useState<RGOption[]>([]);
  const [vnets, setVnets] = useState<VNetOption2[]>([]);
  const [subnets, setSubnets] = useState<SubnetOption2[]>([]);
  const [rgsLoading, setRgsLoading] = useState(false);
  const [vnetsLoading, setVnetsLoading] = useState(false);
  const [subnetsLoading, setSubnetsLoading] = useState(false);

  const [recent, setRecent] = useState<Deployment[]>([]);
  const [activeDeployment, setActiveDeployment] = useState<Deployment | null>(null);
  const [wizardStep, setWizardStep] = useState(0);
  const [form, setForm] = useState<FormData>(defaultForm());
  const [showWizard, setShowWizard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deploying, setDeploying] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [templates, setTemplates] = useState<DeploymentTemplate[]>(() => loadTemplates());
  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);

  const loadCatalog = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/deployment/catalog`);
      setCatalog(res.data);
    } catch {}
  }, []);

  const loadCaps = useCallback(async (location: string) => {
    setCapsLoading(true);
    setCaps(null);
    try {
      const res = await axios.get(`${API_URL}/deployment/capabilities/${location}`);
      setCaps(res.data);
    } catch {}
    finally { setCapsLoading(false); }
  }, []);

  const loadSubscriptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/resources/subscriptions`);
      setSubscriptions(res.data.map((s: any) => ({ id: s.id, name: s.name })));
    } catch {}
  }, []);

  const loadResourceGroups = useCallback(async (subscriptionId: string) => {
    setRgsLoading(true);
    setResourceGroups([]);
    try {
      const res = await axios.get(`${API_URL}/deployment/resource-groups?subscriptionId=${subscriptionId}`);
      setResourceGroups(res.data);
    } catch {} finally { setRgsLoading(false); }
  }, []);

  const loadVnets = useCallback(async (subscriptionId: string, location: string) => {
    setVnetsLoading(true);
    setVnets([]);
    setSubnets([]);
    try {
      const res = await axios.get(`${API_URL}/deployment/vnets?subscriptionId=${subscriptionId}&location=${location}`);
      setVnets(res.data);
    } catch {} finally { setVnetsLoading(false); }
  }, []);

  const loadSubnets = useCallback(async (subscriptionId: string, vnetName: string) => {
    setSubnetsLoading(true);
    setSubnets([]);
    try {
      const res = await axios.get(`${API_URL}/deployment/subnets?subscriptionId=${subscriptionId}&vnetName=${vnetName}`);
      setSubnets(res.data);
    } catch {} finally { setSubnetsLoading(false); }
  }, []);

  const loadRecent = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/deployment`);
      setRecent(res.data);
    } catch {}
  }, []);

  const TERMINAL = ['Succeeded', 'Failed', 'Cancelled'];

  const pollDeployment = useCallback(async (id: string) => {
    try {
      const res = await axios.get(`${API_URL}/deployment/${id}`);
      const d: Deployment = res.data;
      setActiveDeployment(d);
      if (TERMINAL.includes(d.state)) await loadRecent();
    } catch {}
  }, [loadRecent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drive polling from activeDeployment state — cleans up on unmount AND on terminal state
  useEffect(() => {
    if (!activeDeployment || TERMINAL.includes(activeDeployment.state)) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(() => pollDeployment(activeDeployment.deploymentId), 2000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [activeDeployment?.state, activeDeployment?.deploymentId, pollDeployment]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadCatalog();
    loadSubscriptions();
    loadRecent().finally(() => setLoading(false));
  }, [loadCatalog, loadSubscriptions, loadRecent]);

  // Re-fetch capabilities whenever the wizard is open and location changes
  useEffect(() => {
    if (showWizard && form.location) loadCaps(form.location);
  }, [form.location, showWizard, loadCaps]);

  // Cascade: subscription change → reload resource groups
  useEffect(() => {
    if (form.subscriptionId) loadResourceGroups(form.subscriptionId);
    else setResourceGroups([]);
  }, [form.subscriptionId, loadResourceGroups]);

  // Cascade: subscription + location change → reload VNets
  useEffect(() => {
    if (form.subscriptionId && form.location) loadVnets(form.subscriptionId, form.location);
    else setVnets([]);
  }, [form.subscriptionId, form.location, loadVnets]);

  // Cascade: vnetName change → reload subnets (only for existing VNets, not new ones)
  useEffect(() => {
    if (form.subscriptionId && form.vnetName && !form.createNewVnet) loadSubnets(form.subscriptionId, form.vnetName);
    else setSubnets([]);
  }, [form.subscriptionId, form.vnetName, form.createNewVnet, loadSubnets]);

  const update = (field: keyof FormData, value: any) => {
    setForm(f => {
      const next = { ...f, [field]: value };
      if (field === 'subscriptionId') {
        // Reset downstream selections
        next.resourceGroup = ''; next.createNewResourceGroup = false;
        next.vnetName = '';      next.createNewVnet = false;
        next.subnetName = '';    next.createNewSubnet = false;
      }
      if (field === 'location') {
        next.vmSize = ''; next.availabilityZone = null;
        next.vnetName = ''; next.createNewVnet = false;
        next.subnetName = ''; next.createNewSubnet = false;
      }
      if (field === 'vmSize') next.availabilityZone = null;
      return next;
    });
    setErrors(e => { const n = { ...e }; delete n[field as string]; return n; });
  };

  const updateTag = (i: number, k: 'key' | 'value', v: string) => {
    const tags = [...form.tags];
    tags[i] = { ...tags[i], [k]: v };
    setForm(f => ({ ...f, tags }));
  };

  const validateStep = (): boolean => {
    const e: Record<string, string> = {};
    if (wizardStep === 0) {
      if (!form.name)           e.name = 'Required';
      if (!form.subscriptionId) e.subscriptionId = 'Required';
      if (!form.resourceGroup)  e.resourceGroup = 'Required';
      if (!form.location)       e.location = 'Required';
    }
    if (wizardStep === 1) {
      if (!form.osImage)        e.osImage = 'Required';
      if (!form.vmSize)         e.vmSize = 'Required';
      if (!form.adminUsername)  e.adminUsername = 'Required';
      if (form.vmSize && caps) {
        const detail = caps.sizeDetails.find(d => d.sizeId === form.vmSize);
        if (detail && !detail.available) e.vmSize = detail.unavailableReason || 'Not available in this region.';
      }
    }
    if (wizardStep === 2) {
      if (!form.vnetName)   e.vnetName = 'Required';
      if (!form.subnetName) e.subnetName = 'Required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      const payload = {
        name: form.name,
        subscriptionId: form.subscriptionId,
        resourceGroup: form.resourceGroup,
        createNewResourceGroup: form.createNewResourceGroup,
        location: form.location,
        availabilityZone: form.availabilityZone,
        vmSize: form.vmSize,
        osType: form.osType,
        osImage: form.osImage,
        adminUsername: form.adminUsername,
        authType: form.authType,
        sshPublicKey: form.sshPublicKey || null,
        adminPassword: form.adminPassword || null,
        osDiskSizeGb: form.osDiskSizeGb,
        osDiskType: form.osDiskType,
        dataDisks: [],
        network: {
          vnetName: form.vnetName,
          createNewVnet: form.createNewVnet,
          vnetAddressSpace: form.vnetAddressSpace,
          subnetName: form.subnetName,
          subnetAddressPrefix: form.subnetAddressPrefix,
          assignPublicIp: form.assignPublicIp,
        },
        tags: form.tags.reduce((acc: Record<string, string>, t) => { if (t.key) acc[t.key] = t.value; return acc; }, {}),
      };

      const res = await axios.post(`${API_URL}/deployment`, payload);
      if (res.data.approvalRequired) {
        showToast('Deployment submitted for approval — track it in the Approvals tab.', 'info');
        setShowWizard(false);
        setWizardStep(0);
        setForm(defaultForm());
        return;
      }
      const dep: Deployment = res.data;
      setActiveDeployment(dep); // polling useEffect picks this up automatically
      setShowWizard(false);
      setWizardStep(0);
      setForm(defaultForm());
    } catch (e: any) {
      showToast(extractErrorMessage(e, 'Deployment request failed.'), 'error');
    } finally {
      setDeploying(false);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await axios.post(`${API_URL}/deployment/${id}/cancel`);
      await pollDeployment(id);
    } catch (e) {
      showToast(extractErrorMessage(e, 'Failed to cancel deployment.'), 'error');
    }
  };

  const selectedSize = catalog?.vmSizes.find(s => s.id === form.vmSize);
  const filteredImages = catalog?.osImages.filter(i => i.osType === form.osType) || [];

  const handleSaveTemplate = () => {
    if (!templateName.trim()) return;
    const { name: _name, ...rest } = form;
    const t: DeploymentTemplate = { id: crypto.randomUUID(), name: templateName.trim(), savedAt: new Date().toISOString(), form: rest };
    saveTemplate(t);
    setTemplates(loadTemplates());
    setTemplateName('');
    setShowSaveTemplate(false);
    showToast(`Template "${t.name}" saved.`, 'success');
  };

  const handleApplyTemplate = (t: DeploymentTemplate) => {
    setForm(f => ({ ...t.form, name: f.name }));
    setShowWizard(true);
    setWizardStep(0);
    loadCaps(t.form.location);
    showToast(`Template "${t.name}" applied. Fill in the VM name and deploy.`, 'info');
  };

  const handleDeleteTemplate = (id: string) => {
    deleteTemplate(id);
    setTemplates(loadTemplates());
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>VM Deployment</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#888', fontSize: '0.9rem' }}>Provision new virtual machines with guided wizard</p>
        </div>
        <button className="btn-primary" onClick={() => { const open = !showWizard; setShowWizard(open); setWizardStep(0); if (open) loadCaps(form.location); }}>
          {showWizard ? '✕ Cancel' : '+ Deploy VM'}
        </button>
      </div>

      {/* Templates panel */}
      {templates.length > 0 && !showWizard && (
        <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: 8, padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.8rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.6rem' }}>Saved Templates</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {templates.map(t => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 0, background: '#1a1a1e', border: '1px solid #555', borderRadius: 6, overflow: 'hidden' }}>
                <button
                  onClick={() => handleApplyTemplate(t)}
                  style={{ background: 'transparent', border: 'none', color: '#f0f0f0', padding: '0.3rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }}
                  title={`Saved ${new Date(t.savedAt).toLocaleDateString()}`}
                >
                  📋 {t.name}
                </button>
                <button
                  onClick={() => handleDeleteTemplate(t.id)}
                  style={{ background: 'transparent', border: 'none', borderLeft: '1px solid #333', color: '#666', padding: '0.3rem 0.5rem', cursor: 'pointer', fontSize: '0.75rem' }}
                  title="Delete template"
                >✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active deployment progress */}
      {activeDeployment && (
        <ProgressPanel
          deployment={activeDeployment}
          onClose={() => setActiveDeployment(null)}
          onCancel={handleCancel}
        />
      )}

      {/* Deployment wizard */}
      {showWizard && catalog && (
        <div style={{ background: '#25252a', border: '1px solid #444', borderRadius: 8, padding: '1.5rem', marginBottom: '1.5rem' }}>
          {/* Wizard progress */}
          <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem' }}>
            {WIZARD_STEPS.map((s, i) => (
              <div key={s} style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: i < wizardStep ? '#28a745' : i === wizardStep ? '#007bff' : '#333',
                    color: i <= wizardStep ? 'white' : '#666', fontWeight: 700, fontSize: '0.8rem', marginBottom: 4,
                  }}>{i < wizardStep ? '✓' : i + 1}</div>
                  <span style={{ fontSize: '0.75rem', color: i === wizardStep ? '#fff' : '#666' }}>{s}</span>
                </div>
                {i < WIZARD_STEPS.length - 1 && (
                  <div style={{ height: 2, flex: 1, background: i < wizardStep ? '#28a745' : '#333', marginBottom: 22 }} />
                )}
              </div>
            ))}
          </div>

          {/* Step 0: Basics */}
          {wizardStep === 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>

              {/* VM Name */}
              <div>
                <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>VM Name *</label>
                <input value={form.name} onChange={e => update('name', e.target.value)}
                  placeholder="e.g. prod-api-04" style={{ width: '100%', boxSizing: 'border-box' }} />
                {errors.name && <div style={{ color: '#ff8f9a', fontSize: '0.75rem', marginTop: 2 }}>{errors.name}</div>}
              </div>

              {/* Location */}
              <div>
                <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Location *</label>
                <select value={form.location} onChange={e => update('location', e.target.value)} style={{ width: '100%' }}>
                  {catalog.locations.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>

              {/* Subscription */}
              <div>
                <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Subscription *</label>
                <select value={form.subscriptionId} onChange={e => update('subscriptionId', e.target.value)} style={{ width: '100%' }}>
                  <option value="">— Select subscription —</option>
                  {subscriptions.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {errors.subscriptionId && <div style={{ color: '#ff8f9a', fontSize: '0.75rem', marginTop: 2 }}>{errors.subscriptionId}</div>}
              </div>

              {/* Resource Group */}
              <SelectOrCreate
                label="Resource Group"
                required
                options={resourceGroups.map(rg => ({ value: rg.name, label: rg.name, sublabel: rg.location }))}
                selectedValue={form.resourceGroup}
                isCreating={form.createNewResourceGroup}
                newValue={form.resourceGroup}
                newPlaceholder="New resource group name"
                error={errors.resourceGroup}
                disabled={!form.subscriptionId}
                loading={rgsLoading}
                emptyMessage={form.subscriptionId ? 'No resource groups in this subscription' : undefined}
                onSelect={v => setForm(f => ({ ...f, resourceGroup: v, createNewResourceGroup: false }))}
                onCreate={() => setForm(f => ({ ...f, resourceGroup: '', createNewResourceGroup: true }))}
                onNewValueChange={v => setForm(f => ({ ...f, resourceGroup: v }))}
                onCancelCreate={() => setForm(f => ({ ...f, resourceGroup: '', createNewResourceGroup: false }))}
              />

            </div>
          )}

          {/* Step 1: OS & Size */}
          {wizardStep === 1 && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, color: '#b0b0b0', fontSize: '0.85rem' }}>OS Type *</label>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    {['Linux', 'Windows'].map(t => (
                      <button key={t} type="button" onClick={() => { update('osType', t); update('osImage', ''); }}
                        style={{ flex: 1, padding: '0.5rem', borderRadius: 6, border: '1px solid', borderColor: form.osType === t ? '#007bff' : '#444', background: form.osType === t ? '#007bff22' : 'transparent', color: form.osType === t ? '#4da6ff' : '#888', cursor: 'pointer' }}>
                        {t === 'Linux' ? '🐧 Linux' : '🪟 Windows'}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Admin Username *</label>
                  <input value={form.adminUsername} onChange={e => update('adminUsername', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: 8, color: '#b0b0b0', fontSize: '0.85rem' }}>OS Image *</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                  {filteredImages.map(img => (
                    <div key={img.id} onClick={() => update('osImage', img.id)}
                      style={{ padding: '0.75rem', borderRadius: 6, border: '1px solid', borderColor: form.osImage === img.id ? '#007bff' : '#444', background: form.osImage === img.id ? '#007bff11' : '#1e1e23', cursor: 'pointer', position: 'relative' }}>
                      {img.isRecommended && <span style={{ position: 'absolute', top: 4, right: 4, background: '#28a74533', color: '#28a745', fontSize: '0.65rem', borderRadius: 3, padding: '1px 4px' }}>Recommended</span>}
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: form.osImage === img.id ? '#4da6ff' : '#f0f0f0' }}>{img.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#888', marginTop: 2 }}>{img.publisher}</div>
                    </div>
                  ))}
                </div>
                {(errors as any).osImage && <div style={{ color: '#ff8f9a', fontSize: '0.75rem', marginTop: 2 }}>{(errors as any).osImage}</div>}
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ color: '#b0b0b0', fontSize: '0.85rem' }}>VM Size *</label>
                  {capsLoading && <span style={{ color: '#888', fontSize: '0.8rem' }}>Checking availability for {form.location}...</span>}
                  {!capsLoading && caps && <span style={{ color: '#888', fontSize: '0.8rem' }}>{caps.availableVmSizes.length}/{catalog.vmSizes.length} sizes available in {caps.location}</span>}
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#333338' }}>
                        {['', 'Size', 'vCPUs', 'Memory (GB)', 'Storage (GB)', '$/hr', 'Zones', 'Availability'].map(h => (
                          <th key={h} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', color: '#888', fontWeight: 600 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {catalog.vmSizes.map(s => {
                        const avail = caps?.sizeDetails.find(d => d.sizeId === s.id);
                        const isUnavailable = avail ? !avail.available : false;
                        const isSelected = form.vmSize === s.id;
                        return (
                          <tr key={s.id}
                            onClick={() => !isUnavailable && update('vmSize', s.id)}
                            style={{ cursor: isUnavailable ? 'not-allowed' : 'pointer', opacity: isUnavailable ? 0.45 : 1, background: isSelected ? '#007bff11' : undefined, borderBottom: '1px solid #333' }}>
                            <td style={{ padding: '0.5rem 0.75rem' }}>
                              <div style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid', borderColor: isSelected ? '#007bff' : '#555', background: isSelected ? '#007bff' : 'transparent' }} />
                            </td>
                            <td style={{ padding: '0.5rem 0.75rem', fontWeight: isSelected ? 600 : 400, color: isSelected ? '#4da6ff' : '#f0f0f0' }}>{s.name}</td>
                            <td style={{ padding: '0.5rem 0.75rem', color: '#b0b0b0' }}>{s.vCpus}</td>
                            <td style={{ padding: '0.5rem 0.75rem', color: '#b0b0b0' }}>{s.memoryGb}</td>
                            <td style={{ padding: '0.5rem 0.75rem', color: '#b0b0b0' }}>{s.storageGb}</td>
                            <td style={{ padding: '0.5rem 0.75rem', color: s.pricePerHour > 0.2 ? '#ffc107' : '#28a745' }}>${s.pricePerHour.toFixed(3)}</td>
                            <td style={{ padding: '0.5rem 0.75rem', color: '#b0b0b0', fontSize: '0.8rem' }}>
                              {caps ? (avail && avail.availableInZones.length > 0 ? avail.availableInZones.map(z => `Z${z}`).join(', ') : '—') : '…'}
                            </td>
                            <td style={{ padding: '0.5rem 0.75rem' }}>
                              {caps ? (
                                isUnavailable
                                  ? <span style={{ color: '#dc3545', fontSize: '0.75rem' }}>✗ {avail?.unavailableReason}</span>
                                  : <span style={{ color: '#28a745', fontSize: '0.75rem' }}>✓ Available</span>
                              ) : <span style={{ color: '#555', fontSize: '0.75rem' }}>Checking…</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {(errors as any).vmSize && <div style={{ background: '#dc354522', border: '1px solid #dc354566', borderRadius: 4, padding: '0.4rem 0.6rem', color: '#ff8f9a', fontSize: '0.8rem', marginTop: 6 }}>{(errors as any).vmSize}</div>}

                {/* Zone picker — only shown when a size is selected and zones are available for it */}
                {form.vmSize && caps && (() => {
                  const avail = caps.sizeDetails.find(d => d.sizeId === form.vmSize);
                  const zones = avail?.availableInZones ?? [];
                  if (zones.length === 0) return (
                    <div style={{ marginTop: 10, padding: '0.5rem 0.75rem', background: '#ffc10711', border: '1px solid #ffc10744', borderRadius: 6, fontSize: '0.8rem', color: '#ffc107' }}>
                      Availability zones not supported for this size in {form.location}. VM will be deployed in a non-zonal configuration.
                    </div>
                  );
                  return (
                    <div style={{ marginTop: 10 }}>
                      <label style={{ display: 'block', marginBottom: 6, color: '#b0b0b0', fontSize: '0.85rem' }}>
                        Availability Zone <span style={{ color: '#555', fontWeight: 400 }}>(optional — leave blank for no zone preference)</span>
                      </label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => update('availabilityZone', null)}
                          style={{ padding: '0.35rem 0.8rem', borderRadius: 4, border: '1px solid', borderColor: form.availabilityZone === null ? '#007bff' : '#555', background: form.availabilityZone === null ? '#007bff22' : 'transparent', color: form.availabilityZone === null ? '#4da6ff' : '#888', cursor: 'pointer', fontSize: '0.8rem' }}>
                          No preference
                        </button>
                        {zones.map(z => (
                          <button key={z} type="button" onClick={() => update('availabilityZone', z)}
                            style={{ padding: '0.35rem 0.8rem', borderRadius: 4, border: '1px solid', borderColor: form.availabilityZone === z ? '#007bff' : '#555', background: form.availabilityZone === z ? '#007bff22' : 'transparent', color: form.availabilityZone === z ? '#4da6ff' : '#b0b0b0', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                            Zone {z}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div style={{ marginTop: '1rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Auth Type</label>
                  <select value={form.authType} onChange={e => update('authType', e.target.value)} style={{ width: '100%' }}>
                    <option value="SshKey">SSH Public Key</option>
                    <option value="Password">Password</option>
                  </select>
                </div>
                <div>
                  {form.authType === 'SshKey' ? (
                    <>
                      <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>SSH Public Key</label>
                      <input value={form.sshPublicKey} onChange={e => update('sshPublicKey', e.target.value)} placeholder="ssh-rsa AAAA..." style={{ width: '100%', boxSizing: 'border-box' }} />
                    </>
                  ) : (
                    <>
                      <label style={{ display: 'block', marginBottom: 4, color: '#b0b0b0', fontSize: '0.85rem' }}>Admin Password</label>
                      <input type="password" value={form.adminPassword} onChange={e => update('adminPassword', e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Network */}
          {wizardStep === 2 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>

              {/* Virtual Network */}
              <SelectOrCreate
                label="Virtual Network"
                required
                options={vnets.map(v => ({ value: v.name, label: v.name, sublabel: `${v.addressSpace} · ${v.resourceGroup}` }))}
                selectedValue={form.vnetName}
                isCreating={form.createNewVnet}
                newValue={form.vnetName}
                newPlaceholder="New VNet name"
                extraField={
                  <input value={form.vnetAddressSpace} onChange={e => update('vnetAddressSpace', e.target.value)}
                    placeholder="Address space (e.g. 10.0.0.0/16)"
                    style={{ width: '100%', boxSizing: 'border-box' as const, marginTop: 6 }} />
                }
                error={errors.vnetName}
                disabled={!form.subscriptionId || !form.location}
                loading={vnetsLoading}
                emptyMessage={form.subscriptionId && form.location ? `No VNets in ${form.location} for this subscription` : undefined}
                onSelect={v => {
                  const found = vnets.find(x => x.name === v);
                  setForm(f => ({
                    ...f, vnetName: v, createNewVnet: false,
                    vnetAddressSpace: found?.addressSpace ?? f.vnetAddressSpace,
                    subnetName: '', createNewSubnet: false,
                  }));
                }}
                onCreate={() => setForm(f => ({ ...f, vnetName: '', createNewVnet: true, subnetName: '', createNewSubnet: false }))}
                onNewValueChange={v => setForm(f => ({ ...f, vnetName: v }))}
                onCancelCreate={() => setForm(f => ({ ...f, vnetName: '', createNewVnet: false }))}
              />

              {/* Subnet */}
              <SelectOrCreate
                label="Subnet"
                required
                options={subnets.map(s => ({ value: s.name, label: s.name, sublabel: s.addressPrefix }))}
                selectedValue={form.subnetName}
                isCreating={form.createNewSubnet}
                newValue={form.subnetName}
                newPlaceholder="New subnet name"
                extraField={
                  <input value={form.subnetAddressPrefix} onChange={e => update('subnetAddressPrefix', e.target.value)}
                    placeholder="Address prefix (e.g. 10.0.1.0/24)"
                    style={{ width: '100%', boxSizing: 'border-box' as const, marginTop: 6 }} />
                }
                error={errors.subnetName}
                disabled={!form.vnetName && !form.createNewVnet}
                loading={subnetsLoading}
                emptyMessage={form.vnetName ? `No subnets found in ${form.vnetName}` : undefined}
                onSelect={v => {
                  const found = subnets.find(x => x.name === v);
                  setForm(f => ({
                    ...f, subnetName: v, createNewSubnet: false,
                    subnetAddressPrefix: found?.addressPrefix ?? f.subnetAddressPrefix,
                  }));
                }}
                onCreate={() => setForm(f => ({ ...f, subnetName: '', createNewSubnet: true }))}
                onNewValueChange={v => setForm(f => ({ ...f, subnetName: v }))}
                onCancelCreate={() => setForm(f => ({ ...f, subnetName: '', createNewSubnet: false }))}
              />

              {/* Public IP */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: '#b0b0b0', fontSize: '0.85rem' }}>
                  <input type="checkbox" checked={form.assignPublicIp} onChange={e => update('assignPublicIp', e.target.checked)} />
                  Assign public IP address
                </label>
              </div>

              {/* Info when upstream selections are missing */}
              {!form.subscriptionId && (
                <div style={{ gridColumn: '1 / -1', color: '#ffc107', fontSize: '0.85rem', background: '#ffc10711', border: '1px solid #ffc10733', borderRadius: 6, padding: '0.6rem 0.75rem' }}>
                  Go back to Basics and select a subscription first — VNet options depend on it.
                </div>
              )}

            </div>
          )}

          {/* Step 3: Tags */}
          {wizardStep === 3 && (
            <div>
              <div style={{ marginBottom: '0.75rem', color: '#b0b0b0', fontSize: '0.85rem' }}>Resource tags applied to the VM on deployment.</div>
              {form.tags.map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input placeholder="Key" value={t.key} onChange={e => updateTag(i, 'key', e.target.value)} style={{ flex: 1 }} />
                  <input placeholder="Value" value={t.value} onChange={e => updateTag(i, 'value', e.target.value)} style={{ flex: 1 }} />
                  <button className="btn-small btn-danger" onClick={() => setForm(f => ({ ...f, tags: f.tags.filter((_, j) => j !== i) }))}>✕</button>
                </div>
              ))}
              <button className="btn-small btn-secondary" onClick={() => setForm(f => ({ ...f, tags: [...f.tags, { key: '', value: '' }] }))}>+ Add Tag</button>
            </div>
          )}

          {/* Step 4: Review */}
          {wizardStep === 4 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div>
                <h4 style={{ margin: '0 0 0.75rem', color: '#888', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Basics</h4>
                {[
                  ['Name', form.name],
                  ['Subscription', subscriptions.find(s => s.id === form.subscriptionId)?.name ?? form.subscriptionId],
                  ['Resource Group', `${form.resourceGroup}${form.createNewResourceGroup ? ' (new)' : ''}`],
                  ['Location', form.location],
                  ['Avail. Zone', form.availabilityZone != null ? `Zone ${form.availabilityZone}` : 'No preference'],
                ].map(([k, v]) => <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: '0.85rem' }}><span style={{ color: '#888', width: 130 }}>{k}:</span><span>{v}</span></div>)}
              </div>
              <div>
                <h4 style={{ margin: '0 0 0.75rem', color: '#888', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>OS & Compute</h4>
                {[
                  ['OS Type', form.osType],
                  ['OS Image', catalog.osImages.find(i => i.id === form.osImage)?.name || form.osImage],
                  ['VM Size', selectedSize?.name || form.vmSize],
                  ['vCPUs', String(selectedSize?.vCpus || '—')],
                  ['Memory', selectedSize ? `${selectedSize.memoryGb} GB` : '—'],
                  ['Est. Cost', selectedSize ? `$${(selectedSize.pricePerHour * 730).toFixed(0)}/mo` : '—'],
                ].map(([k, v]) => <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: '0.85rem' }}><span style={{ color: '#888', width: 130 }}>{k}:</span><span>{v}</span></div>)}
              </div>
              <div>
                <h4 style={{ margin: '0 0 0.75rem', color: '#888', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Network</h4>
                {[
                  ['VNet', `${form.vnetName}${form.createNewVnet ? ' (new)' : ''}`],
                  ['Subnet', `${form.subnetName}${form.createNewSubnet ? ' (new)' : ''}`],
                  ['Public IP', form.assignPublicIp ? 'Yes' : 'No'],
                ].map(([k, v]) => <div key={k} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: '0.85rem' }}><span style={{ color: '#888', width: 130 }}>{k}:</span><span>{v}</span></div>)}
              </div>
              <div>
                <h4 style={{ margin: '0 0 0.75rem', color: '#888', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tags ({form.tags.filter(t => t.key).length})</h4>
                {form.tags.filter(t => t.key).map(t => (
                  <div key={t.key} style={{ fontSize: '0.85rem', marginBottom: 4 }}>
                    <span style={{ background: '#444', borderRadius: 3, padding: '1px 6px' }}>{t.key}: {t.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Navigation */}
          <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #444' }}>
            {/* Save as template (Review step only) */}
            {wizardStep === WIZARD_STEPS.length - 1 && (
              <div style={{ marginBottom: '1rem' }}>
                {showSaveTemplate ? (
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input
                      value={templateName}
                      onChange={e => setTemplateName(e.target.value)}
                      placeholder="Template name…"
                      style={{ background: '#1a1a1e', color: '#f0f0f0', border: '1px solid #555', borderRadius: 5, padding: '0.35rem 0.65rem', fontSize: '0.875rem', flex: 1 }}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveTemplate(); if (e.key === 'Escape') setShowSaveTemplate(false); }}
                      autoFocus
                    />
                    <button onClick={handleSaveTemplate} style={{ background: '#28a745', color: 'white', border: 'none', borderRadius: 5, padding: '0.35rem 0.75rem', cursor: 'pointer', fontSize: '0.875rem' }}>Save</button>
                    <button onClick={() => setShowSaveTemplate(false)} style={{ background: 'transparent', border: '1px solid #555', color: '#888', borderRadius: 5, padding: '0.35rem 0.65rem', cursor: 'pointer', fontSize: '0.875rem' }}>✕</button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowSaveTemplate(true)}
                    style={{ background: 'transparent', border: '1px solid #555', color: '#b0b0b0', borderRadius: 5, padding: '0.35rem 0.75rem', cursor: 'pointer', fontSize: '0.825rem' }}
                  >
                    📋 Save as Template
                  </button>
                )}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <button className="btn-secondary" onClick={() => setWizardStep(s => s - 1)} disabled={wizardStep === 0}>← Previous</button>
              {wizardStep < WIZARD_STEPS.length - 1 ? (
                <button className="btn-primary" onClick={() => { if (validateStep()) setWizardStep(s => s + 1); }}>Next →</button>
              ) : (
                <button className="btn-primary" onClick={handleDeploy} disabled={deploying || !canWrite}
                  title={canWrite ? undefined : 'Requires the operator role'}>
                  {deploying ? 'Starting deployment...' : '🚀 Deploy VM'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Recent deployments */}
      {!loading && <RecentDeployments deployments={recent} onView={d => setActiveDeployment(d)} />}
    </div>
  );
};

export default VmDeployment;
